/**
 * TalentLens AI - CVs List Page
 * Redesigned for better UX with candidate-focused display
 */

import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import api from '../lib/api';
import { useAuthStore, canDeleteCV, canExport } from '../store/auth';
import { useWebSocket } from '../lib/websocket';

type FilterStatus = 'all' | 'processed' | 'pending' | 'processing' | 'failed';

export default function CVsPage() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [cursor, setCursor] = useState<string | null>(null);
  const [selectedCvs, setSelectedCvs] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);

  // WebSocket for real-time CV processing updates
  const { isConnected, subscribe, subscribeToServer, unsubscribeFromServer } = useWebSocket();

  // Handle WebSocket updates for processing CVs
  useEffect(() => {
    if (!isConnected || !user) return;

    // Subscribe to all processing events
    const unsubscribe = subscribe('*', (message: any) => {
      const eventType = message.type;

      // When a CV finishes processing (completed or failed), refresh the list
      if (eventType === 'processing_completed' || eventType === 'processing_failed') {
        // Invalidate the CVs query to refresh the list
        queryClient.invalidateQueries({ queryKey: ['cvs'] });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [isConnected, user, subscribe, queryClient]);

  const { data: cvsData, isLoading, isFetching } = useQuery({
    queryKey: ['cvs', filter, cursor],
    queryFn: () => api.getCVs({
      ...(filter !== 'all' && { status: filter }),
      limit: '25',
      ...(cursor && { cursor }),
    }),
    // Smart polling: faster when CVs are processing and WebSocket not connected
    // When WebSocket is connected, we rely on real-time updates
    refetchInterval: (query) => {
      const cvs = query.state.data?.data?.cvs || [];
      const hasProcessing = cvs.some((cv: any) =>
        cv.status === 'pending' || cv.status === 'processing'
      );

      if (isConnected) {
        // WebSocket connected: poll less frequently as fallback
        return hasProcessing ? 10000 : 60000;
      } else {
        // No WebSocket: poll more frequently for real-time feel
        return hasProcessing ? 3000 : 30000;
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteCV(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cvs'] });
      setSelectedCvs(new Set());
    },
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => api.retryCV(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cvs'] }),
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => api.archiveCV(id, archived),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cvs'] }),
  });

  const cvs = cvsData?.data?.cvs || [];
  const pagination = cvsData?.data?.pagination;
  const hasMore = pagination?.hasMore || false;
  const nextCursor = pagination?.nextCursor;

  // Get processing CV IDs for WebSocket subscription
  const processingCvIds = useMemo(() => {
    return cvs
      .filter((cv: any) => cv.status === 'pending' || cv.status === 'processing')
      .map((cv: any) => cv.id);
  }, [cvs]);

  // Subscribe to server for processing CVs
  useEffect(() => {
    if (!isConnected || !user || processingCvIds.length === 0) return;

    // Subscribe to each processing CV
    processingCvIds.forEach((cvId: string) => {
      subscribeToServer(cvId, user.id);
    });

    return () => {
      // Unsubscribe when component unmounts or CVs change
      processingCvIds.forEach((cvId: string) => {
        unsubscribeFromServer(cvId);
      });
    };
  }, [isConnected, user, processingCvIds, subscribeToServer, unsubscribeFromServer]);

  // Calculate counts for each status
  const statusCounts = cvs.reduce((acc: Record<string, number>, cv: any) => {
    acc[cv.status] = (acc[cv.status] || 0) + 1;
    return acc;
  }, {});

  const filterOptions: { key: FilterStatus; label: string; count?: number }[] = [
    { key: 'all', label: 'All', count: cvs.length },
    { key: 'processed', label: 'Processed', count: statusCounts['processed'] || 0 },
    { key: 'pending', label: 'Pending', count: statusCounts['pending'] || 0 },
    { key: 'processing', label: 'Processing', count: statusCounts['processing'] || 0 },
    { key: 'failed', label: 'Failed', count: statusCounts['failed'] || 0 },
  ];

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const cvIds = selectedCvs.size > 0 ? Array.from(selectedCvs) : undefined;
      const csv = await api.exportCVsCSV(cvIds);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `candidates-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportJSON = async () => {
    setIsExporting(true);
    try {
      const cvIds = selectedCvs.size > 0 ? Array.from(selectedCvs) : undefined;
      const data = await api.exportCVsJSON(cvIds);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `candidates-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const toggleSelectCv = (cvId: string) => {
    const newSelected = new Set(selectedCvs);
    if (newSelected.has(cvId)) {
      newSelected.delete(cvId);
    } else {
      newSelected.add(cvId);
    }
    setSelectedCvs(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedCvs.size === cvs.length) {
      setSelectedCvs(new Set());
    } else {
      setSelectedCvs(new Set(cvs.map((cv: any) => cv.id)));
    }
  };

  const handleBulkDelete = () => {
    if (!confirm(`Delete ${selectedCvs.size} selected CVs?`)) return;
    selectedCvs.forEach(id => deleteMutation.mutate(id));
  };

  return (
    <div className="space-y-6">
      {/* Header with filters and actions */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Candidates</h1>
            <p className="text-gray-600">
              {cvs.length} candidate{cvs.length !== 1 ? 's' : ''} in your talent pool
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Export buttons */}
            {canExport(user) && (
              <div className="relative group">
                <button
                  disabled={isExporting}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  <ExportIcon className="w-4 h-4" />
                  Export
                  {selectedCvs.size > 0 && ` (${selectedCvs.size})`}
                </button>
                <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 hidden group-hover:block z-10">
                  <button
                    onClick={handleExportCSV}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 rounded-t-lg"
                  >
                    Export as CSV
                  </button>
                  <button
                    onClick={handleExportJSON}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 rounded-b-lg"
                  >
                    Export as JSON
                  </button>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
              {filterOptions.map((option) => (
                <button
                  key={option.key}
                  onClick={() => { setFilter(option.key); setCursor(null); }}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    filter === option.key
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {option.label}
                  {option.count !== undefined && option.count > 0 && (
                    <span className={`ml-1.5 text-xs ${
                      filter === option.key ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      {option.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Batch actions bar */}
        {selectedCvs.size > 0 && (
          <div className="flex items-center gap-4 px-4 py-3 bg-primary-50 rounded-lg">
            <span className="text-sm font-medium text-primary-700">
              {selectedCvs.size} selected
            </span>
            <div className="flex items-center gap-2">
              {canDeleteCV(user) && (
                <button
                  onClick={handleBulkDelete}
                  className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-100 rounded-lg"
                >
                  Delete Selected
                </button>
              )}
              <button
                onClick={() => setSelectedCvs(new Set())}
                className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Select all checkbox */}
      {cvs.length > 0 && (
        <div className="flex items-center gap-2 px-2">
          <input
            type="checkbox"
            checked={selectedCvs.size === cvs.length && cvs.length > 0}
            onChange={toggleSelectAll}
            className="rounded text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-600">Select all</span>
        </div>
      )}

      {/* CV List */}
      {isLoading ? (
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full" />
                <div className="flex-1 space-y-3">
                  <div className="h-5 bg-gray-200 rounded w-1/3" />
                  <div className="h-4 bg-gray-200 rounded w-1/4" />
                  <div className="flex gap-2">
                    <div className="h-6 bg-gray-200 rounded w-16" />
                    <div className="h-6 bg-gray-200 rounded w-20" />
                    <div className="h-6 bg-gray-200 rounded w-14" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : cvs.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <>
          <div className="grid gap-4">
            {cvs.map((cv: any) => (
              <CandidateCard
                key={cv.id}
                cv={cv}
                cvId={cv.id}
                isSelected={selectedCvs.has(cv.id)}
                onSelect={() => toggleSelectCv(cv.id)}
                onDelete={canDeleteCV(user) ? () => deleteMutation.mutate(cv.id) : undefined}
                onRetry={(cv.status === 'failed' || cv.status === 'pending') ? () => retryMutation.mutate(cv.id) : undefined}
                onArchive={() => archiveMutation.mutate({ id: cv.id, archived: !cv.isArchived })}
                isDeleting={deleteMutation.isPending}
                isRetrying={retryMutation.isPending}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between pt-4">
            <button
              onClick={() => setCursor(null)}
              disabled={!cursor || isFetching}
              className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back to Start
            </button>
            <span className="text-sm text-gray-500">
              {isFetching ? 'Loading...' : `Showing ${cvs.length} candidates`}
            </span>
            <button
              onClick={() => setCursor(nextCursor)}
              disabled={!hasMore || isFetching}
              className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Load More
            </button>
          </div>
        </>
      )}
    </div>
  );
}

interface CandidateCardProps {
  cv: any;
  cvId: string;
  isSelected?: boolean;
  onSelect?: () => void;
  onDelete?: () => void;
  onRetry?: () => void;
  onArchive?: () => void;
  isDeleting?: boolean;
  isRetrying?: boolean;
}

function CandidateCard({ cv, cvId, isSelected, onSelect, onDelete, onRetry, onArchive, isDeleting, isRetrying }: CandidateCardProps) {
  const candidateName = cv.parsedData?.name || (cv.status === 'failed' ? cv.filename : 'Processing...');
  const candidateEmail = cv.parsedData?.email;
  const skills = cv.parsedData?.skills || [];
  const latestExperience = cv.parsedData?.experience?.[0];
  const isProcessed = cv.status === 'processed';
  const isPending = cv.status === 'pending' || cv.status === 'processing';
  const isFailed = cv.status === 'failed';
  const failureReason = cv.parseErrors?.[0];
  const isDuplicate = !!cv.duplicateOf;
  const isArchived = cv.isArchived;

  // Get initials for avatar
  const initials = candidateName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  // Calculate total years of experience
  const totalYears = skills.reduce((sum: number, s: any) => Math.max(sum, s.years || 0), 0);

  return (
    <div className={`relative ${isArchived ? 'opacity-60' : ''} ${isPending ? 'animate-pulse-subtle' : ''}`}>
      <Link
        to="/cvs/$cvId"
        params={{ cvId }}
        className={`block bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all border-l-4 ${
          isProcessed ? 'border-l-green-500' :
          isPending ? 'border-l-yellow-500 bg-yellow-50/50 dark:bg-yellow-900/10' :
          isFailed ? 'border-l-red-500' : 'border-l-gray-300'
        } ${isSelected ? 'ring-2 ring-primary-500' : ''}`}
      >
        <div className="p-5">
          <div className="flex items-start gap-4">
            {/* Checkbox */}
            <div onClick={(e) => { e.preventDefault(); onSelect?.(); }}>
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => {}}
                className="rounded text-primary-600 focus:ring-primary-500 cursor-pointer"
              />
            </div>

            {/* Avatar */}
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 ${
              isProcessed ? 'bg-primary-600' :
              isPending ? 'bg-yellow-500' :
              isFailed ? 'bg-red-500' : 'bg-gray-400'
            }`}>
              {isPending ? (
                <LoadingSpinner className="w-5 h-5" />
              ) : (
                initials || '?'
              )}
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {/* Name and badges */}
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {candidateName}
                    </h3>
                    <StatusBadge status={cv.status} />
                    {isDuplicate && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                        Duplicate
                      </span>
                    )}
                    {isArchived && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                        Archived
                      </span>
                    )}
                  </div>

                  {/* Email and experience */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mb-3">
                    {candidateEmail && (
                      <span className="flex items-center gap-1">
                        <EmailIcon className="w-4 h-4" />
                        {candidateEmail}
                      </span>
                    )}
                    {latestExperience && (
                      <span className="flex items-center gap-1">
                        <BriefcaseIcon className="w-4 h-4" />
                        {latestExperience.title} at {latestExperience.company}
                      </span>
                    )}
                    {totalYears > 0 && (
                      <span className="flex items-center gap-1">
                        <ClockIcon className="w-4 h-4" />
                        {totalYears}+ years exp.
                      </span>
                    )}
                  </div>

                  {/* Skills */}
                  {skills.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {skills.slice(0, 6).map((skill: any, i: number) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded-full text-xs font-medium"
                        >
                          {skill.skill}
                        </span>
                      ))}
                      {skills.length > 6 && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                          +{skills.length - 6} more
                        </span>
                      )}
                    </div>
                  )}

                  {/* Failure reason */}
                  {isFailed && failureReason && (
                    <div className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 mb-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                      </svg>
                      <span className="truncate">{failureReason}</span>
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>
                      Added {new Date(cv.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                    {cv.uploadedBy?.name && (
                      <>
                        <span className="text-gray-300">|</span>
                        <span>by {cv.uploadedBy.name}</span>
                      </>
                    )}
                    <span className="text-gray-300">|</span>
                    <span className="text-gray-400">{cv.filename}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  {onRetry && (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRetry(); }}
                      disabled={isRetrying}
                      className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                      title="Retry processing"
                    >
                      {isRetrying ? (
                        <LoadingSpinner className="w-5 h-5" />
                      ) : (
                        <RetryIcon className="w-5 h-5" />
                      )}
                    </button>
                  )}
                  {onArchive && (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onArchive(); }}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      title={isArchived ? "Unarchive" : "Archive"}
                    >
                      <ArchiveIcon className="w-5 h-5" />
                    </button>
                  )}
                  <span
                    className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="View CV"
                  >
                    <ViewIcon className="w-5 h-5" />
                  </span>
                  {onDelete && (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); if(confirm('Delete this CV?')) onDelete(); }}
                      disabled={isDeleting}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete CV"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}

function EmptyState({ filter }: { filter: string }) {
  const messages: Record<string, { title: string; description: string }> = {
    all: {
      title: 'No candidates yet',
      description: 'Upload CVs using the button in the header to start building your talent pool.'
    },
    processed: {
      title: 'No processed CVs',
      description: 'CVs that have been successfully parsed will appear here.'
    },
    pending: {
      title: 'No pending CVs',
      description: 'CVs waiting to be processed will appear here.'
    },
    processing: {
      title: 'No CVs being processed',
      description: 'CVs currently being analyzed will appear here.'
    },
    failed: {
      title: 'No failed CVs',
      description: 'Good news! All your CVs have been processed successfully.'
    },
  };

  const { title, description } = messages[filter] || messages.all;

  return (
    <div className="text-center py-16 bg-white rounded-xl shadow-sm">
      <DocumentIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 max-w-md mx-auto">{description}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; label: string; showSpinner?: boolean }> = {
    processed: { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-700 dark:text-green-300', label: 'Processed' },
    pending: { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-700 dark:text-yellow-300', label: 'In Queue', showSpinner: true },
    processing: { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-700 dark:text-blue-300', label: 'Processing...', showSpinner: true },
    failed: { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-700 dark:text-red-300', label: 'Failed' },
    partial: { bg: 'bg-orange-100 dark:bg-orange-900/50', text: 'text-orange-700 dark:text-orange-300', label: 'Partial' },
  };

  const { bg, text, label, showSpinner } = config[status] || { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', label: status };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full ${bg} ${text}`}>
      {showSpinner && (
        <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {label}
    </span>
  );
}

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className || 'h-5 w-5'}`} viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function ViewIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function EmailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function BriefcaseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function RetryIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function ArchiveIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
    </svg>
  );
}

function ExportIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}
